import { useState } from "react";
import { T } from "../data/Tokens";
import {
  Card, PageHeader, GoldBtn, GhostBtn, OutlineBtn, StatusPill,
  SearchBar, Th, Td, Tr, Tabs, StatCard, EmptyState,
  SectionTitle, Field, Input, Textarea, Select, Toggle, ConfirmModal
} from "../components/common/Ui";

const BLOGS_INIT = [
  { id:"B001", title:"5 Daily Planner Habits That Changed My Life",  category:"Productivity", author:"Aishcraft Team", date:"10 May 2025", status:"Published", views:2841, tags:["planner","habits","productivity"] },
  { id:"B002", title:"How to Choose the Right Journal for You",       category:"Journals",     author:"Priya R.",      date:"5 May 2025",  status:"Published", views:1932, tags:["journal","guide","self-care"] },
  { id:"B003", title:"The Art of Bullet Journaling for Beginners",   category:"Notebooks",    author:"Aishcraft Team",date:"28 Apr 2025", status:"Draft",     views:0,    tags:["bullet journal","beginner"] },
  { id:"B004", title:"Corporate Gifting Guide 2025: Stationery Picks",category:"Corporate",   author:"Megha K.",      date:"20 Apr 2025", status:"Published", views:3210, tags:["corporate","gifting","2025"] },
  { id:"B005", title:"Wellness Journaling: A Complete Starter Guide", category:"Wellness",    author:"Aishcraft Team",date:"15 Apr 2025", status:"Scheduled", views:0,    tags:["wellness","mental health","journal"] },
];

const STATS = [
  { icon:"✍️", label:"Total Posts",    value:"24",     sub:"All time",          color:T.gold },
  { icon:"✅", label:"Published",      value:"18",     sub:"Live on website",   color:T.success },
  { icon:"📝", label:"Drafts",         value:"4",      sub:"In progress",       color:T.warning, bg:"#FFF8EC" },
  { icon:"📅", label:"Scheduled",      value:"2",      sub:"Upcoming",          color:"#5B7EE5", bg:"#EEF3FF" },
  { icon:"👁️", label:"Total Views",    value:"48,200", sub:"All time",          color:T.success },
  { icon:"📈", label:"Avg Views/Post", value:"2,011",  sub:"Per published post", color:T.gold },
];

const EMPTY_FORM = {
  title:"", category:"Productivity", author:"Aishcraft Team",
  content:"", excerpt:"", tags:[], status:"Draft",
  seoTitle:"", seoDesc:"", featured:false,
};

export default function BlogsAdmin() {
  const [blogs, setBlogs]     = useState(BLOGS_INIT);
  const [tab, setTab]         = useState("all");
  const [search, setSearch]   = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [del, setDel]         = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [tagInput, setTagInput] = useState("");

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setF("tags", [...form.tags, t]);
    setTagInput("");
  };

  const openAdd = () => { setForm(EMPTY_FORM); setEditing(null); setShowForm(true); };
  const openEdit = (b) => { setForm({ ...b }); setEditing(b.id); setShowForm(true); };

  const handleSave = () => {
    if (!form.title.trim()) return;
    if (editing) {
      setBlogs(prev => prev.map(b => b.id === editing ? { ...form, id:editing, views:b.views, date:b.date } : b));
    } else {
      setBlogs(prev => [...prev, { ...form, id:`B${Date.now()}`, date: new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"}), views:0 }]);
    }
    setShowForm(false); setEditing(null);
  };

  const handleDelete = (id) => { setBlogs(b => b.filter(x => x.id !== id)); setDel(null); };

  const filtered = blogs.filter(b => {
    const ms = !search || b.title.toLowerCase().includes(search.toLowerCase());
    const mt = tab === "all" || b.status.toLowerCase() === tab.toLowerCase();
    return ms && mt;
  });

  const TABS = [
    { id:"all",       label:"All",       count: blogs.length },
    { id:"Published", label:"Published", count: blogs.filter(b=>b.status==="Published").length },
    { id:"Draft",     label:"Drafts",    count: blogs.filter(b=>b.status==="Draft").length },
    { id:"Scheduled", label:"Scheduled", count: blogs.filter(b=>b.status==="Scheduled").length },
  ];

  return (
    <div>
      <PageHeader title="Blog CMS" subtitle={`${blogs.length} blog posts`}>
        <GhostBtn>↓ Export</GhostBtn>
        <GoldBtn onClick={openAdd}>+ New Post</GoldBtn>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
        {STATS.map(s => <StatCard key={s.label} {...s} />)}
      </div>

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <Card>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 sm:gap-3 px-4 sm:px-5 py-3.5 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search posts..." />
          <select className="w-full sm:w-auto text-[12px] px-3 py-2.5 rounded-xl outline-none flex-shrink-0"
            style={{ border:`1px solid ${T.border}`, background:"#fff", color:T.textSec }}>
            <option>All Categories</option>
            {["Productivity","Journals","Notebooks","Corporate","Wellness"].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        {filtered.length === 0
          ? <EmptyState icon="✍️" title="No blog posts found" sub="Start writing your first post." action="New Post" onAction={openAdd} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                    <Th>Title</Th><Th>Category</Th><Th>Author</Th>
                    <Th>Date</Th><Th>Views</Th><Th>Status</Th><Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((b, i) => (
                    <Tr key={b.id} last={i === filtered.length - 1}>
                      <Td>
                        <div>
                          <p className="text-[12px] font-semibold" style={{ color:T.textPri }}>{b.title}</p>
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {b.tags.slice(0,2).map(t => (
                              <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full"
                                style={{ background:T.goldL, color:T.goldD }}>{t}</span>
                            ))}
                          </div>
                        </div>
                      </Td>
                      <Td><span className="text-[11px]" style={{ color:T.textSec }}>{b.category}</span></Td>
                      <Td><span className="text-[11px]" style={{ color:T.textSec }}>{b.author}</span></Td>
                      <Td><span className="text-[11px]" style={{ color:T.textSec }}>{b.date}</span></Td>
                      <Td><span className="text-[12px] font-semibold" style={{ color:T.textPri }}>{b.views.toLocaleString()}</span></Td>
                      <Td><StatusPill s={b.status} /></Td>
                      <Td>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(b)}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-amber-50"
                            style={{ borderColor:T.border, color:T.textSec }}>Edit</button>
                          <button onClick={() => setDel(b.id)}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] border hover:bg-red-50"
                            style={{ borderColor:T.border, color:T.danger }}>Delete</button>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      {/* Blog Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex" style={{ background:"rgba(0,0,0,0.5)" }}>
          <div className="m-0 sm:m-auto w-full h-full sm:h-auto sm:max-w-3xl sm:max-h-[90vh] rounded-none sm:rounded-3xl overflow-hidden flex flex-col" style={{ background:T.card }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-2 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0" style={{ borderBottom:`1px solid ${T.border}` }}>
              <div className="flex items-center justify-between sm:contents">
                <h2 className="text-[16px] sm:text-[17px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>
                  {editing ? "Edit Post" : "New Blog Post"}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-gray-400 text-2xl leading-none sm:hidden">×</button>
              </div>
              <div className="flex gap-2">
                <GhostBtn onClick={() => { setF("status","Draft"); handleSave(); }}>Save Draft</GhostBtn>
                <GoldBtn onClick={() => { setF("status","Published"); handleSave(); }}>Publish</GoldBtn>
                <button onClick={() => setShowForm(false)} className="hidden sm:inline text-gray-400 text-xl ml-2">×</button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
              <Field label="Post Title" required>
                <Input value={form.title} onChange={e => setF("title",e.target.value)} placeholder="5 Daily Planner Habits That Changed My Life" />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Category">
                  <Select value={form.category} onChange={e => setF("category",e.target.value)}>
                    {["Productivity","Journals","Notebooks","Corporate","Wellness","Gifting"].map(c=><option key={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label="Author">
                  <Input value={form.author} onChange={e => setF("author",e.target.value)} placeholder="Aishcraft Team" />
                </Field>
                <Field label="Status">
                  <Select value={form.status} onChange={e => setF("status",e.target.value)}>
                    <option>Draft</option><option>Published</option><option>Scheduled</option>
                  </Select>
                </Field>
              </div>
              <Field label="Excerpt">
                <Textarea value={form.excerpt} onChange={e => setF("excerpt",e.target.value)} placeholder="Short summary shown in blog listing..." rows={2} />
              </Field>
              <Field label="Content">
                <Textarea value={form.content} onChange={e => setF("content",e.target.value)} placeholder="Full blog post content..." rows={8} />
              </Field>
              <Field label="Tags">
                <div className="flex gap-2 mb-2 flex-wrap">
                  {form.tags.map(t => (
                    <span key={t} className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full"
                      style={{ background:T.goldL, color:T.goldD }}>
                      {t} <button onClick={() => setF("tags", form.tags.filter(x=>x!==t))} className="hover:text-red-500">×</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag..." />
                  <GoldBtn small onClick={addTag}>Add</GoldBtn>
                </div>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="SEO Title">
                  <Input value={form.seoTitle} onChange={e => setF("seoTitle",e.target.value)} placeholder="SEO optimized title" />
                </Field>
                <Field label="SEO Description">
                  <Input value={form.seoDesc} onChange={e => setF("seoDesc",e.target.value)} placeholder="Meta description" />
                </Field>
              </div>
              <Toggle checked={form.featured} onChange={v => setF("featured",v)} label="Mark as Featured Post" />
            </div>
          </div>
        </div>
      )}

      {del && <ConfirmModal title="Delete Post?" message="This blog post will be permanently deleted."
        onConfirm={() => handleDelete(del)} onCancel={() => setDel(null)} />}
    </div>
  );
}